import type { GameEventDraft, GameRuntime, GameState, PlacementOpts } from './state';
import type { PieceDef, PieceId } from './gameDef';
import type { SeatIdx } from '../types';

// Placement rules BY REGISTRATION (H20-S0d, plan §0 item 5).
//
// "May this seat put this piece on that site?" used to be a function imported by name: the
// building module's road and settlement rules, called directly by the building actions, by
// Road Building and by the bot. So a module that replaced BUILD_ROAD left the other two on
// the old rule — and an owned ship would have made open-water corners look like settlement
// sites to the bot while the validator refused them (the review's phantom-site finding).
//
// Now a module REGISTERS the rule for a piece (`ModuleCtx.placementRule`, ordered override:
// a later module replaces an earlier one) and every consumer asks the registry. The bot can
// no longer disagree with the validator, because it asks the same function.
//
// Content-blind: this file names no piece. Cost and pool are NOT part of a rule — a rule is
// "legal minus cost/pool" (the Road Building reading, outline §14.17), and the caller adds the
// economy that applies to it.

/** Is `site` a legal spot for `piece`, placed by `seat`, ignoring cost and pool? */
export function placementLegal(state: GameState, env: GameRuntime, seat: SeatIdx, piece: PieceId, site: string, opts?: PlacementOpts): boolean {
  const rule = env.composed.placementRules.get(piece);
  return rule ? rule(state, env, seat, site, opts) : false;
}

/** The def's EDGE pieces, in declaration order — Road Building's content-blind menu. */
export function edgePieces(env: GameRuntime): PieceDef[] {
  return env.def.pieces.filter((p) => p.domain === 'edge');
}

/**
 * A piece just went down (built, set up, placed for free, or moved): tell every module that
 * registered interest (H20-S0d). Discovery hangs here — fog is revealed by the placement
 * ITSELF, inside the action, so a rule that depends on what was revealed (Road Building's
 * "can I continue?") is decided after it. In the classic game nothing registers and this is
 * a no-op.
 */
export function notePiecePlaced(state: GameState, env: GameRuntime, seat: SeatIdx, piece: PieceId, site: string, emit: (e: GameEventDraft) => void): void {
  for (const { hook } of env.composed.piecePlaced) hook(state, env, seat, piece, site, emit);
}
